import { useTranslation } from "@i18n";
import { useState } from "react";
import { createTakeoff, unitTypesList } from "@Actions/estimation";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { useDispatch } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS } from "@Services/constants";
import Text from "@CubeComponents/html/Text/Text";

function CreateNewTakeoffModal({ estimateId, closeTakeoffModal, editData, updateTakeOffData, setTakeoffsList }: any) {
  const { t } = useTranslation();
  const [buttonLoading, setButtonLoading] = useState(false);
  const initialTakeoffData = editData || { name: "", description: "", type: "Count" };
  const [newTakeoffDetails, setNewTakeoffDetails] = useState(initialTakeoffData);
  const dispatch = useDispatch();

  const createNewTakeoff = async (event: any) => {
    setButtonLoading((prev) => !prev);
    event.preventDefault();
    newTakeoffDetails.name = newTakeoffDetails.name.trim();
    newTakeoffDetails.description = newTakeoffDetails.description?.trim();
    const { name, description, type } = newTakeoffDetails;
    if (!name) return;

    if (editData) {
      await updateTakeOffData({ ...newTakeoffDetails });
    } else {
      const response = await createTakeoff({
        name,
        description,
        type,
        estimateId,
      });
      if (response.data.status) {
        dispatch(showToastNotification({ content: t("create_takeoff", { name }), type: TOAST_TYPE_SUCCESS }));
        if (setTakeoffsList) {
          setTakeoffsList((prev: any) => [
            {
              name,
              description,
              type,
              estimateId,
            },
            ...prev,
          ]);
        }
      }
    }
    setButtonLoading((prev) => !prev);
    closeTakeoffModal({ refresh: true });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTakeoffDetails((prev: any) => ({ ...prev, name: e.target.value }));
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTakeoffDetails((prev: any) => ({ ...prev, description: e.target.value }));
  };

  const handleTypeChange = (e: any) => {
    setNewTakeoffDetails((prev: any) => ({ ...prev, type: e.value }));
  };

  const header = () => (editData ? t("Edit Takeoff") : t("Create New Takeoff"));

  const content = () => (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <Text>{t("Takeoff Name")}</Text>
        <TextFieldComponent
          value={newTakeoffDetails.name}
          placeholder={t("Enter Takeoff Name")}
          onChange={handleNameChange}
        />
      </div>

      {Object.hasOwn(initialTakeoffData, "description") && (
        <div className="flex flex-col gap-3">
          <Text>{t("Takeoff Description")}</Text>
          <TextFieldComponent
            value={newTakeoffDetails.description}
            placeholder={t("Enter Takeoff Description")}
            onChange={handleDescriptionChange}
          />
        </div>
      )}
      {Object.hasOwn(initialTakeoffData, "type") && (
        <div className="flex flex-col gap-3">
          <Text>{t("Takeoff Type")}</Text>
          <AutoComplete
            options={unitTypesList}
            valueField="name"
            getOptionLabel={(option: any) => option?.name || ""}
            value={unitTypesList.find((opt: any) => opt.name === newTakeoffDetails.type) || null}
            onChange={(_event: any, newValue: any) => {
              handleTypeChange({ value: newValue?.name });
            }}
            slotProps={{ popper: { sx: { zIndex: "3001 !important" } }, listbox: { sx: { padding: 0 } } }}
            renderInput={(params: any) => <TextFieldComponent {...params} placeholder={t("Takeoff Type")} />}
          />
        </div>
      )}
    </div>
  );

  const footer = () => (
    <div className="flex items-center gap-5">
      <ButtonComponent
        disabled={!newTakeoffDetails.name.trim()}
        onClick={createNewTakeoff}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        loading={buttonLoading}
      >
        <Text>{editData ? t("Save") : t("Create")}</Text>
      </ButtonComponent>

      <ButtonComponent onClick={closeTakeoffModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      width="28.125rem"
      isVisible
      headerTitle={header()}
      zindex={3000}
      onCancel={closeTakeoffModal}
      modelContent={content()}
      footerContent={footer()}
    />
  );
}

CreateNewTakeoffModal.defaultProps = {
  editData: null,
  setTakeoffsList: undefined,
};

export default CreateNewTakeoffModal;
